import { getAmountExcludingVat } from '@features/invoices/utils/getAmountExcludingVat'
import { getAmountIncludingVat } from '@features/invoices/utils/getAmountIncludingVat'
import { roundAmount } from '@features/reconciliation/utils/roundAmount'

import { formatDate } from '../../../utils'
import type { ReviewEntryDataFormValues, ReviewEntryLineItemFormValues } from '../types/getReviewEntryDataFormSchema'
import type { ValidateReviewEntryPayload } from '../types/validateReviewEntryBill'

type PartialReviewEntryFormValues = Partial<Omit<ReviewEntryDataFormValues, 'entryLines'>> & {
  entryLines?: Partial<ReviewEntryLineItemFormValues>[]
}

interface BuildValidateReviewEntryPayloadProps {
  formValues: PartialReviewEntryFormValues
  journalCode?: string
}

interface LineAmounts {
  amountExclTax: number
  amountInclTax: number
  taxAmount: number
}

const getLineAmounts = (line: Partial<ReviewEntryLineItemFormValues>): LineAmounts => {
  const amountExclVat = line.amountExclVat
  const amountInclVat = line.amountInclVat
  const taxRate = line.taxRate
  const vatAmount = line.vatAmount

  if (amountExclVat != null && amountInclVat != null) {
    return {
      amountExclTax: roundAmount(amountExclVat),
      amountInclTax: roundAmount(amountInclVat),
      taxAmount: roundAmount(vatAmount ?? amountInclVat - amountExclVat),
    }
  }

  if (amountExclVat != null && taxRate != null) {
    const amountInclTax = roundAmount(getAmountIncludingVat(amountExclVat, taxRate))

    return {
      amountExclTax: roundAmount(amountExclVat),
      amountInclTax,
      taxAmount: roundAmount(vatAmount ?? amountInclTax - amountExclVat),
    }
  }

  if (amountInclVat != null && taxRate != null) {
    const amountExclTax = roundAmount(getAmountExcludingVat(amountInclVat, taxRate))

    return {
      amountExclTax,
      amountInclTax: roundAmount(amountInclVat),
      taxAmount: roundAmount(vatAmount ?? amountInclVat - amountExclTax),
    }
  }

  return {
    amountExclTax: roundAmount(amountExclVat ?? 0),
    amountInclTax: roundAmount(amountInclVat ?? 0),
    taxAmount: roundAmount(vatAmount ?? 0),
  }
}

export const buildValidateReviewEntryPayload = (
  props: BuildValidateReviewEntryPayloadProps,
): ValidateReviewEntryPayload => {
  const { formValues, journalCode } = props
  const formEntryLines = formValues.entryLines ?? []

  return {
    contactId: formValues.supplier?.id ?? '',
    currencyId: formValues.currencyId ?? '',
    entryDate: formValues.receiptDate ? formatDate(formValues.receiptDate) : null,
    entryLines: formEntryLines.map((line) => {
      const { amountExclTax, amountInclTax, taxAmount } = getLineAmounts(line)

      return {
        accountId: line.accountId?.trim() ?? '',
        accountNumber: line.accountNumber ?? '',
        amountExclTax,
        amountInclTax,
        description: line.description ?? null,
        side: line.side ?? 'debit',
        taxAmount,
        taxRateId: line.vatRateId?.trim() ?? '',
        vatAccountNumber: line.vatAccountNumber ?? null,
      }
    }),
    journalCode: journalCode ?? null,
    // Billy bill `lineDescription` is readonly on create; the advisor API maps `label` to it and gets 422.
    label: null,
    reference: formValues.invoiceNumber ?? null,
  }
}
